import { useEffect, useMemo, useRef, useState } from "react";
import { Image as ImageIcon, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import InstagramCard from "@/components/InstagramCard";

/**
 * Reusable Instagram-card downloader. Renders an offscreen 1080×1350
 * card via `InstagramCard` and exposes a single button that rasterises
 * the card to a PNG via html-to-image on click. The same component is
 * used inside `CitationBlock`'s QR popover (variant="pill") and in
 * page headers next to the "Abrir no YouTube" link (variant="inline").
 *
 * The card lives offscreen so its memory footprint is negligible and
 * it never participates in the page layout — placing two instances of
 * this component on the same page is fine.
 *
 * Robustness for foreign-origin thumbnails (Conjur, JOTA, glbimg, …):
 *   1. On mount we preflight `cardData.thumbnail` via a CORS-mode
 *      `fetch`. If the remote server does not advertise CORS, we drop
 *      the URL and let `InstagramCard` render the "DOUTRINA" fallback
 *      hero. This prevents the html-to-image canvas from getting
 *      tainted on click.
 *   2. If `toPng` still throws (e.g. font fetch race), we retry once
 *      after flipping the local `coverDisabled` flag to force the
 *      no-thumb fallback path — guarantees the download always
 *      succeeds even when an editor pastes a brand-new remote image.
 */
export default function InstagramCardButton({
  cardData,
  qrUrl,
  kindLabel = "midia",
  variant = "pill",
  testid = "card-download",
  className = "",
}) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Tracks whether the foreign-origin thumbnail can be safely embedded
  // in the offscreen canvas. Defaults to true so the card renders with
  // the cover on first paint; the preflight effect below flips it to
  // false the moment a CORS error is detected.
  const [coverDisabled, setCoverDisabled] = useState(false);

  const thumbnail = cardData?.thumbnail || "";
  const isSameOriginThumb = useMemo(() => {
    if (!thumbnail) return true;
    try {
      const u = new URL(thumbnail, window.location.href);
      return u.origin === window.location.origin;
    } catch {
      return false;
    }
  }, [thumbnail]);

  useEffect(() => {
    let cancelled = false;
    // No thumb → nothing to preflight, and the card renders fallback.
    if (!thumbnail) {
      setCoverDisabled(false);
      return;
    }
    // Same-origin → CORS never an issue.
    if (isSameOriginThumb) {
      setCoverDisabled(false);
      return;
    }
    // Foreign origin → probe with a CORS request. If the server omits
    // Access-Control-Allow-Origin, the fetch rejects and we mark the
    // cover unusable for this card. We do NOT show a toast here — the
    // fallback hero looks great on its own.
    fetch(thumbnail, { mode: "cors", credentials: "omit" })
      .then((r) => {
        if (!cancelled) setCoverDisabled(!r.ok);
      })
      .catch(() => {
        if (!cancelled) setCoverDisabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [thumbnail, isSameOriginThumb]);

  const effectiveCardData = useMemo(() => {
    if (!cardData) return cardData;
    return coverDisabled ? { ...cardData, thumbnail: "" } : cardData;
  }, [cardData, coverDisabled]);

  const safeKind = useMemo(() => {
    const base = (kindLabel || "midia")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return base || "midia";
  }, [kindLabel]);

  const rasterise = () =>
    toPng(cardRef.current, {
      width: 1080,
      height: 1350,
      pixelRatio: 1,
      cacheBust: true,
      backgroundColor: "#020617",
    });

  const downloadCard = async () => {
    const node = cardRef.current;
    if (!node) {
      toast.error("Card ainda carregando. Aguarde um instante e tente de novo.");
      return;
    }
    setBusy(true);
    try {
      let dataUrl;
      try {
        dataUrl = await rasterise();
      } catch (firstErr) {
        // Most common failure: foreign-origin cover image taints the
        // canvas despite the preflight. Drop the thumbnail and retry.
        if (!coverDisabled && thumbnail) {
          setCoverDisabled(true);
          // Wait two frames for React to flush the fallback render.
          await new Promise((r) =>
            requestAnimationFrame(() => requestAnimationFrame(r))
          );
          dataUrl = await rasterise();
        } else {
          throw firstErr;
        }
      }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `juristube-card-${safeKind}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Card pronto. Já pode postar no Instagram ou LinkedIn!");
    } catch (e) {
      toast.error(
        "Não foi possível gerar o card. Tente recarregar a página e clicar de novo."
      );
    } finally {
      setBusy(false);
    }
  };

  const pillClass =
    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/40 bg-slate-900/80 hover:bg-slate-800 text-amber-200 text-xs font-semibold transition disabled:opacity-60 disabled:cursor-wait";
  const inlineClass =
    "inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-sm font-medium disabled:opacity-60 disabled:cursor-wait transition";
  const variantClass = variant === "inline" ? inlineClass : pillClass;

  if (!cardData) return null;

  return (
    <>
      <button
        type="button"
        onClick={downloadCard}
        disabled={busy}
        className={`${variantClass} ${className}`}
        data-testid={testid}
        title="Gera um card 1080×1350 com a thumb, título, QR Code e selo JurisTube — pronto para postar no Instagram, LinkedIn ou stories."
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ImageIcon className="w-4 h-4" />
        )}
        {busy ? "Gerando card…" : "Baixar card p/ Instagram"}
      </button>

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-99999px",
          top: "-99999px",
          pointerEvents: "none",
        }}
      >
        <InstagramCard ref={cardRef} data={effectiveCardData} qrUrl={qrUrl} />
      </div>
    </>
  );
}
